import { apiService } from 'services/ServiceRegistry';
import navigation from 'services/NavigationService';

/**
 * Our FormHooksModule for Asset Request Form
 * Here we create our logic for submit and parsing form data
 * @type {{onError(*, *, *): function(...[*]=), submitForm(*=, *=): void, getFormData(*): {end_date: *, delivery_contact_phone: (*), cost_centre_id: *, customer_contact_name: *, delivery_special_instructions: *, delivery_contact_name: (*), additional_information: *, account_id: *, project_id: *, customer_site_id: *, delivery_site_id: *, customer_contact_phone: *, asset_requests: [{manufacturer_id: *, quantity: number, product_id: *}], start_date: *}, onSuccess(*, *=, *): function(...[*]=)}}
 */
const FormHooksModule = () => {
  /**
   * Gets form data and parser into correct format for request
   * @param form
   */
  const getFormAssets = (form: Object) => {
    const assets = form.$('assetDetails').value;

    return assets.map((asset, index) => {
      return {
        manufacturer_id: form
          .$('assetDetails')
          .$(index)
          .$('manufacturer').extra.selectID,
        product_id: form
          .$('assetDetails')
          .$(index)
          .$('model').extra.selectID,
        quantity: form
          .$('assetDetails')
          .$(index)
          .$('quantity').value,
        additional_info: form
          .$('assetDetails')
          .$(index)
          .$('additionalInformation').value,
      };
    });
  };

  /**
   * Gets form data
   * @param form
   */
  const getFormData = (form: Object) => {
    const accountId = form.$('chooseAccount.tradingAccount').extra.selectID;
    const projectId = form.$('assignProjects.list').extra.selectID;
    const costCentreId = form.$('assignCostCentre.list').extra.selectID;
    const customerSiteId = form.$('customerDetails.site').extra.selectID;
    const {
      requiredDates,
      additionalInformation,
      customerDetails,
      deliveryDetails,
    } = form.values();
    const deliverySiteId = customerDetails.deliveryIsEqual
      ? customerSiteId
      : form.$('deliveryDetails.site').extra.selectID;
    const assets = getFormAssets(form);

    return {
      account_id: accountId,
      start_date: requiredDates.startDate,
      end_date: requiredDates.endDate,
      project_id: projectId,
      cost_centre_id: costCentreId,
      additional_information: additionalInformation.details,
      customer_site_id: customerSiteId,
      customer_contact_name: customerDetails.name,
      customer_contact_phone: customerDetails.phoneNo,
      delivery_site_id: deliverySiteId,
      delivery_contact_name: customerDetails.deliveryIsEqual
        ? customerDetails.name
        : deliveryDetails.name,
      delivery_contact_phone: customerDetails.deliveryIsEqual
        ? customerDetails.phoneNo
        : deliveryDetails.phoneNo,
      delivery_special_instructions: deliveryDetails.specialInstructions,
      asset_requests: assets,
    };
  };

  /**
   * Submit form
   * Gathers and sends form information to the API
   * @param formData
   * @param localState
   */
  const submitForm = (formData, localState, t) => {
    apiService
      .createResource('asset-requests', formData)
      .then((response: Object) => {
        localState.isLoading = false;
        localState.responseMessage = t('general:messages.save_success');
        setTimeout(() => {
          localState.showResponseMessage = false;
          /*eslint-disable max-nested-callbacks*/
          setTimeout(() => {
            // Snackbar animation 225ms
            navigation.navigateTo(`/sent-requests/${response.sentRequestId}`);
          }, 225);
        }, 1200);
      })
      .catch((err: string) => {
        localState.isLoading = false;
        localState.responseMessage =
          err && err.errors && err.errors.name && err.errors.name.length > 0
            ? err.errors.name[0]
            : err && err.message;
        localState.hasSuccess = false;
        localState.hasError = true;
      });
  };

  return {
    /**
     * onSuccess Closure
     * Returns our hook function
     * Upper scope params are fields, local state & translate object
     * @returns {function(...[*]=)}
     */
    onSuccess(fields: Object, localState: Object, t: Function) {
      return (form: Object) => {
        localState.isLoading = true;
        localState.responseMessage = t('general:messages.saving_form');
        localState.showResponseMessage = true;
        localState.hasError = false;
        localState.hasSuccess = true;

        const data = getFormData(form);

        submitForm(data, localState, t);
      };
    },

    /**
     * onError Closure
     *  * Returns our hook function
     * Upper scope params are fields, local state & translate object
     * @returns {function(...[*]=)}
     */
    onError(fields: Object, localState: Object, t: Function) {
      return (form: Object) => {
        localState.isLoading = false;
        localState.responseMessage = t('general:messages.form_error');
        localState.showResponseMessage = true;
        localState.hasSuccess = false;
        localState.hasError = true;
      };
    },
  };
};

export const onSuccess = FormHooksModule().onSuccess;
export const onError = FormHooksModule().onError;
